import * as Sentry from "@sentry/react";
import {
	type ApiErrorNames,
	CHECKOUT_PLAN_IDS,
	type CheckoutAcknowledgmentAct,
	type CheckoutAcknowledgmentDefinition,
	type CheckoutPlan,
	type CheckoutPlanId,
	type CheckoutReview,
} from "@coffitivity/shared";
import type { IApiClient } from "@/lib/__shared__/apiClient";

type RequestResponse<T> = [T | null, Error | null];
type CheckoutSessionStatus = "open" | "complete" | null;

/**
 * Error that preserves the API's machine-readable `code` for the caller. The
 * shared `isClientOutdatedError` predicate reads that `code` to detect a stale
 * bundle without depending on this class.
 */
class PaymentRequestError extends Error {
	code?: ApiErrorNames;
	constructor(message: string, code?: ApiErrorNames) {
		super(message);
		this.name = "PaymentRequestError";
		this.code = code;
	}
}

export const PLAN_IDS = CHECKOUT_PLAN_IDS;
export type PlanId = CheckoutPlanId;
export type AcknowledgmentDefinition = CheckoutAcknowledgmentDefinition;
export type { CheckoutPlan, CheckoutReview };

const isCurrentRecurringPlan = (review: CheckoutReview) =>
	(review.currentPlan?.interval === "MONTH" ||
		review.currentPlan?.interval === "YEAR") &&
	review.currentPlan.status !== "canceled";

export const mustManageExistingCheckoutPlan = (
	review: CheckoutReview,
	selectedPlan: CheckoutPlan,
) =>
	review.currentPlan?.interval === "LIFETIME" ||
	(selectedPlan.isSubscription && isCurrentRecurringPlan(review));

export interface AvailablePlans {
	freePlan: {
		name: string;
		description: string;
		features: string[];
	};
	subscriptionPlans: {
		name: string;
		description: string;
		features: string[];
		planTier: "PREMIUM" | "PLUS";
		monthlyVariation: {
			planId: keyof typeof PLAN_IDS;
			totalPrice: number;
			monthlyPrice: number;
			interval: "MONTH";
		};
		yearlyVariation: {
			planId: keyof typeof PLAN_IDS;
			totalPrice: number;
			monthlyPrice: number;
			interval: "YEAR";
		};
	}[];
	lifetimePlan: {
		name: string;
		description: string;
		features: string[];
		planId: keyof typeof PLAN_IDS;
		totalPrice: number;
	};
}

const API_BASE = import.meta.env.VITE_USER_SERVER_URL;

export class PaymentManager {
	private apiClient: IApiClient;

	constructor(apiClient: IApiClient) {
		this.apiClient = apiClient;
	}

	async createCheckoutSession(
		planId: PlanId,
		reviewContext: string,
		acknowledgments: CheckoutAcknowledgmentAct[],
	): Promise<RequestResponse<{ clientSecret: string; sessionId: string }>> {
		try {
			const response = await this.apiClient.fetch(
				`${API_BASE}/payment/create-checkout-session`,
				{
					method: "POST",
					body: JSON.stringify({ planId, reviewContext, acknowledgments }),
				},
			);

			const json: {
				data: { clientSecret: string; sessionId: string };
				message: string;
				code?: ApiErrorNames;
			} = await response.json();

			if (response.ok) {
				return [json.data, null];
			}

			throw new PaymentRequestError(json.message, json.code);
		} catch (err) {
			console.error(`Error: ${err}`);
			Sentry.captureException(err);
			return [null, err as Error];
		}
	}

	async getCheckoutReview(
		planId: PlanId,
		countryOverride?: string,
	): Promise<RequestResponse<CheckoutReview>> {
		try {
			const params = new URLSearchParams({ planId });
			if (countryOverride) {
				params.set("checkoutCountry", countryOverride);
			}
			const response = await this.apiClient.fetch(
				`${API_BASE}/payment/checkout-review?${params.toString()}`,
				{ method: "GET" },
			);
			const json: { data: CheckoutReview; message: string } =
				await response.json();

			if (response.ok) return [json.data, null];
			throw new Error(json.message);
		} catch (err) {
			console.error(`Error: ${err}`);
			Sentry.captureException(err);
			return [null, err as Error];
		}
	}

	async expireCheckoutSession(
		sessionId: string,
	): Promise<RequestResponse<{ expired: boolean }>> {
		try {
			const response = await this.apiClient.fetch(
				`${API_BASE}/payment/expire-checkout-session`,
				{
					method: "POST",
					body: JSON.stringify({ sessionId }),
				},
			);
			const json: { data: { expired: boolean }; message: string } =
				await response.json();
			if (response.ok) return [json.data, null];
			throw new Error(json.message);
		} catch (err) {
			console.error(`Error: ${err}`);
			Sentry.captureException(err);
			return [null, err as Error];
		}
	}

	async getAvailablePlans(): Promise<AvailablePlans> {
		try {
			const response = await this.apiClient.fetch(
				`${API_BASE}/payment/available-plans`,
				{
					method: "GET",
				},
			);
			const json: { data: AvailablePlans; message: string } =
				await response.json();

			if (response.ok) {
				return json.data;
			}

			throw new Error(json.message);
		} catch (err) {
			console.error(`Error: ${err}`);
			Sentry.captureException(err);
			throw err;
		}
	}

	async getCheckoutSession(sessionId: string): Promise<
		RequestResponse<{
			id: string;
			startDate: string;
			endDate: string;
			publishedPriceUsd: number | null;
			status: CheckoutSessionStatus;
			interval: string;
			planTier: string;
			customerEmail: string;
		}>
	> {
		try {
			const response = await this.apiClient.fetch(
				`${API_BASE}/payment/checkout-session?sessionId=${encodeURIComponent(sessionId)}`,
				{
					method: "GET",
				},
			);

			const json: {
				data: {
					status: CheckoutSessionStatus;
					customerEmail: string;
					id: string;
					startDate: string;
					endDate: string;
					publishedPriceUsd: number | null;
					interval: string;
					planTier: string;
				};
				message: string;
			} = await response.json();

			if (response.ok) {
				return [json.data, null];
			}

			throw new Error(json.message);
		} catch (err) {
			console.error(`Error: ${err}`);
			Sentry.captureException(err);
			return [null, err as Error];
		}
	}
}
